/**
 * Load a lesson sequence and persist practice completion / history on the profile.
 */

import { updateEntity } from '../data/entities';
import { getLessonSequence } from './lessonSequence';
import { normalizeSequence, sequenceTotalSeconds } from './sequenceModel';

export const HISTORY_LIMIT = 12;

export function loadPracticeSequence(lesson, planType) {
  return getLessonSequence(lesson, planType);
}

function localDateKey(ms = Date.now()) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function yesterdayKey(ms = Date.now()) {
  return localDateKey(ms - 24 * 60 * 60 * 1000);
}

/**
 * Update streak / sessions after a completed practice.
 * Streak uses local calendar dates.
 */
export function nextPracticeStats(profile, now = Date.now()) {
  const today = localDateKey(now);
  const last = profile?.lastPracticedAt ? localDateKey(profile.lastPracticedAt) : null;
  let streakDays = Number(profile?.streakDays) || 0;
  if (last === today) {
    // Already practiced today — keep streak, still count session.
  } else if (last === yesterdayKey(now)) {
    streakDays += 1;
  } else {
    streakDays = 1;
  }
  const totalSessions = (Number(profile?.totalSessions) || 0) + 1;
  return {
    streakDays,
    lastPracticedAt: now,
    totalSessions,
  };
}

function sanitizeHistoryEntry(entry, now = Date.now()) {
  if (!entry?.lessonId) return null;
  const practicedSeconds = Math.max(0, Math.round(Number(entry.practicedSeconds) || 0));
  return {
    lessonId: String(entry.lessonId).slice(0, 120),
    title: String(entry.title || 'Class').trim().slice(0, 160) || 'Class',
    completedAt: Number(entry.completedAt) || now,
    practicedSeconds,
    style: entry.style ? String(entry.style).slice(0, 40) : undefined,
    skillLevel: entry.skillLevel ? String(entry.skillLevel).slice(0, 40) : undefined,
  };
}

/** Newest-first list, capped. */
export function appendPracticeHistory(profile, entry, now = Date.now()) {
  const next = sanitizeHistoryEntry(entry, now);
  if (!next) return Array.isArray(profile?.practiceHistory) ? profile.practiceHistory : [];
  const prev = Array.isArray(profile?.practiceHistory) ? profile.practiceHistory : [];
  return [next, ...prev].slice(0, HISTORY_LIMIT);
}

export function readPracticeHistory(profile) {
  const list = Array.isArray(profile?.practiceHistory) ? profile.practiceHistory : [];
  return list
    .map((h) => sanitizeHistoryEntry(h))
    .filter(Boolean)
    .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
}

/**
 * What Today should offer as Continue:
 * - resume: left mid-class
 * - again: last completed class (when different enough to show)
 */
export function continueTarget(profile, { recommendedId } = {}) {
  const prog = profile?.inProgressPractice;
  if (prog?.lessonId && Number.isFinite(Number(prog.stepIndex))) {
    return {
      kind: 'resume',
      lessonId: String(prog.lessonId),
      title: String(prog.title || 'Class'),
      stepIndex: Math.max(0, Math.floor(Number(prog.stepIndex) || 0)),
      updatedAt: Number(prog.updatedAt) || 0,
    };
  }
  const history = readPracticeHistory(profile);
  const last = history[0];
  const lessonId = last?.lessonId || (profile?.lastLessonId ? String(profile.lastLessonId) : null);
  if (!lessonId) return null;
  if (recommendedId && lessonId === recommendedId) return null;
  return {
    kind: 'again',
    lessonId,
    title: last?.title || 'Last class',
    stepIndex: 0,
    completedAt: last?.completedAt || profile?.lastPracticedAt || 0,
  };
}

export function practicePath(target) {
  if (!target?.lessonId) return '/library';
  if (target.kind === 'resume' && target.stepIndex > 0) {
    return `/practice/${target.lessonId}?step=${target.stepIndex}`;
  }
  return `/practice/${target.lessonId}`;
}

/**
 * @param {object} meta — { lessonId, title, practicedSeconds, style?, skillLevel? }
 */
export async function recordPracticeCompletion(uid, profile, meta = {}) {
  if (!uid || !profile) return null;
  const now = Date.now();
  const stats = nextPracticeStats(profile, now);
  const practiceHistory = appendPracticeHistory(
    profile,
    {
      lessonId: meta.lessonId,
      title: meta.title,
      practicedSeconds: meta.practicedSeconds,
      style: meta.style,
      skillLevel: meta.skillLevel,
      completedAt: now,
    },
    now,
  );
  const patch = {
    ...stats,
    practiceHistory,
    inProgressPractice: null,
  };
  if (meta.lessonId) {
    patch.lastLessonId = String(meta.lessonId).slice(0, 120);
  }
  await updateEntity('userProfile', {}, uid, patch);
  return patch;
}

export async function saveInProgressPractice(uid, bookmark) {
  if (!uid || !bookmark?.lessonId) return null;
  const patch = {
    inProgressPractice: {
      lessonId: String(bookmark.lessonId).slice(0, 120),
      title: String(bookmark.title || 'Class').trim().slice(0, 160) || 'Class',
      stepIndex: Math.max(0, Math.floor(Number(bookmark.stepIndex) || 0)),
      updatedAt: Date.now(),
    },
  };
  await updateEntity('userProfile', {}, uid, patch);
  return patch;
}

export async function clearInProgressPractice(uid, profile) {
  if (!uid || !profile?.inProgressPractice) return null;
  await updateEntity('userProfile', {}, uid, { inProgressPractice: null });
  return { inProgressPractice: null };
}

export function practicedSeconds(sequence, completedStepCount, remainingOnCurrent) {
  const seq = normalizeSequence(sequence);
  let done = 0;
  for (let i = 0; i < completedStepCount && i < seq.length; i += 1) {
    done += seq[i].holdSeconds;
  }
  if (completedStepCount < seq.length) {
    const hold = seq[completedStepCount]?.holdSeconds || 0;
    done += Math.max(0, hold - (remainingOnCurrent || 0));
  }
  return done;
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export function formatRelativeDay(ms) {
  if (!ms) return '';
  const day = localDateKey(ms);
  const today = localDateKey();
  if (day === today) return 'Today';
  if (day === yesterdayKey()) return 'Yesterday';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function plannedDurationSeconds(sequence) {
  return sequenceTotalSeconds(sequence);
}
